import { DatePipe, DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { VehicleTripData, VehicleTripResponse } from 'lcmm-lib-js';
import { secondsToDuration2 } from 'src/app/utils/utils';

enum DetailType {
  string,
  number,
  boolean,
  date,
  period,
}

interface AttributeInfo {
  key: string;
  detailType: DetailType;
  formatString?: string;
  currencies?: string[];
}

interface TableRow {
  vehicleId: string;
  isOverview: boolean;
  rawData: { [key: string]: unknown };
  ROWLABEL: string;
  [key: string]: unknown;
}

@Component({
  selector: 'app-trip-vehicle-compare-dialog',
  standalone: false,
  templateUrl: './trip-vehicle-compare-dialog.component.html',
  styleUrl: './trip-vehicle-compare-dialog.component.scss',
})
export class TripVehicleCompareDialogComponent implements AfterViewInit {
  // Public properties
  public locale: string;

  public displayedColumns: string[] = [];

  public tableData = new MatTableDataSource<TableRow>([]);

  public errorMsg: string | null = null;

  // Private properties
  private attributeInfos: AttributeInfo[] = [];

  private vehicleTripResponse: VehicleTripResponse;

  private vehicleIds: string[] = [];

  // View children
  @ViewChild(MatSort) public sort: MatSort;

  constructor(
    public dialog: MatDialog,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private ts: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      vehicleTripResponse: VehicleTripResponse;
      vehicleId?: string[];
      vehicleName?: string[];
    }
  ) {
    this.locale = ts.instant('LOCALE');
    this.vehicleTripResponse = data.vehicleTripResponse;
    this.vehicleIds = Array.isArray(data.vehicleId) ? data.vehicleId : [];
    this.setupAttributeInfos();
  }

  ngAfterViewInit(): void {
    this.buildTable();
    this.configureSorting();
  }

  private configureSorting(): void {
    if (!this.sort) return;

    this.tableData.sort = this.sort;

    this.tableData.sortingDataAccessor = (item, property) => {
      if (
        property !== 'ROWLABEL' &&
        item.rawData &&
        item.rawData[property] !== undefined
      ) {
        const value = item.rawData[property];
        return typeof value === 'string' || typeof value === 'number'
          ? value
          : '';
      }

      const value = item[property];
      return typeof value === 'string' || typeof value === 'number'
        ? value
        : '';
    };

    const originalSortData = this.tableData.sortData;
    this.tableData.sortData = (data: TableRow[], sort: MatSort) => {
      const overviewRow = data.filter((item) => item.isOverview);
      const vehicleRows = data.filter((item) => !item.isOverview);

      const sortedVehicleRows = originalSortData(vehicleRows, sort);

      return [...overviewRow, ...sortedVehicleRows];
    };
  }

  private setupAttributeInfos(): void {
    this.attributeInfos = [
      {
        key: 'TRIP.COUNT',
        detailType: DetailType.number,
      },
      {
        key: 'TRIP.DISTANCE',
        detailType: DetailType.number,
        currencies: ['km'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.DURATION',
        detailType: DetailType.period,
      },
      {
        key: 'TRIP.AVERAGESPEED',
        detailType: DetailType.number,
        currencies: ['km/h'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.FUELCONSUMPTION',
        detailType: DetailType.number,
        currencies: ['l/100km'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.G_CO2EMISSION',
        detailType: DetailType.number,
        currencies: ['gCO2/km'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.CO2EMISSION',
        detailType: DetailType.number,
        currencies: ['kg'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.PERCENTAGEWLTPLOW',
        detailType: DetailType.number,
        currencies: ['%'],
        formatString: '1.0-0',
      },
      {
        key: 'TRIP.PERCENTAGEWLTPMEDIUM',
        detailType: DetailType.number,
        currencies: ['%'],
        formatString: '1.0-0',
      },
      {
        key: 'TRIP.PERCENTAGEWLTPHIGH',
        detailType: DetailType.number,
        currencies: ['%'],
        formatString: '1.0-0',
      },
      {
        key: 'TRIP.PERCENTAGEWLTPEXTRAHIGH',
        detailType: DetailType.number,
        currencies: ['%'],
        formatString: '1.0-0',
      },
      {
        key: 'TRIP.PERCENTAGEACCCYCLE',
        detailType: DetailType.number,
        currencies: ['%'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.KMAEROWORK',
        detailType: DetailType.number,
        currencies: ['MJ/100km'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.AEROWORK',
        detailType: DetailType.number,
        currencies: ['MJ'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.ENERGYCONSUMPTION',
        detailType: DetailType.number,
        currencies: ['MJ'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.ZEROFUELDISTANCE',
        detailType: DetailType.number,
        currencies: ['km'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.STANDSTILLTIME',
        detailType: DetailType.period,
      },
      {
        key: 'TRIP.STANDSTILLWORK',
        detailType: DetailType.number,
        currencies: ['MJ'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.ACCWORK',
        detailType: DetailType.number,
        currencies: ['MJ'],
        formatString: '1.0-1',
      },

      {
        key: 'TRIP.ROLLWORK',
        detailType: DetailType.number,
        currencies: ['MJ'],
        formatString: '1.0-1',
      },
      {
        key: 'TRIP.GRADEWORK',
        detailType: DetailType.number,
        currencies: ['MJ'],
        formatString: '1.0-1',
      },
    ];
  }

  private buildTable(): void {
    const rows: TableRow[] = [];
    this.createColumns();
    this.createRows(rows);
    this.populateTable(rows);
    this.tableData.data = rows;
  }

  private createRows(rows: TableRow[]): void {
    if (this.vehicleTripResponse.Overview) {
      const overviewRow: TableRow = {
        vehicleId: 'Overview',
        isOverview: true,
        rawData: {},
        ROWLABEL: '',
      };
      rows.push(overviewRow);
    }
    this.vehicleIds.forEach((vehicleId) => {
      if (this.vehicleTripResponse[vehicleId]) {
        const vehicleRow: TableRow = {
          vehicleId,
          isOverview: false,
          rawData: {},
          ROWLABEL: '',
        };
        rows.push(vehicleRow);
      }
    });
  }

  private createColumns(): void {
    this.displayedColumns = ['ROWLABEL'];
    this.attributeInfos.forEach((info) => {
      const columnId = info.key.replace(/\./g, '_');
      this.displayedColumns.push(columnId);
    });
  }

  private populateTable(rows: TableRow[]): void {
    rows.forEach((row) => {
      const { vehicleId } = row;
      const vehicleData = this.vehicleTripResponse[vehicleId];

      if (!vehicleData) return;
      // eslint-disable-next-line no-param-reassign
      row.ROWLABEL = this.getVehicleLabel(vehicleId);

      this.attributeInfos.forEach((info) => {
        const columnId = info.key.replace(/\./g, '_');
        const rawValue = this.extractValue(vehicleData, info.key);
        // eslint-disable-next-line no-param-reassign
        row.rawData[columnId] = rawValue;
        // eslint-disable-next-line no-param-reassign
        row[columnId] = this.formatValue(rawValue, info);
      });
    });
  }

  private getVehicleLabel(vehicleId: string): string {
    if (vehicleId === 'Overview') {
      const vehicleCount =
        this.vehicleTripResponse.Overview?.vehicleCount ||
        this.vehicleIds.length;
      return `${vehicleCount} ${this.ts.instant('VEHICLE.TITLE')}`;
    }

    const vehicleIndex = this.vehicleIds.indexOf(vehicleId);
    if (
      vehicleIndex >= 0 &&
      this.data.vehicleName &&
      this.data.vehicleName[vehicleIndex]
    ) {
      return this.data.vehicleName[vehicleIndex];
    }

    return vehicleId;
  }

  private extractValue(vehicleData: VehicleTripData, attributeKey: string) {
    switch (attributeKey) {
      case 'TRIP.COUNT':
        return vehicleData.tripCount;
      case 'TRIP.DURATION':
        return vehicleData.sumDuration;
      case 'TRIP.DISTANCE':
        return vehicleData.sumDistance;
      case 'TRIP.ZEROFUELDISTANCE':
        return vehicleData.sumZeroFuelDistance / 1000;
      case 'TRIP.STANDSTILLTIME':
        return vehicleData.sumStandStillTime;
      case 'TRIP.ENERGYCONSUMPTION':
        return vehicleData.tripResponse.absoluteCalculation.totalWork / 1e6;
      case 'TRIP.CO2EMISSION':
        return vehicleData.sumEmission;
      case 'TRIP.ACCWORK':
        return vehicleData.sumAccWork / 1e6;
      case 'TRIP.AEROWORK':
        return vehicleData.sumAeroWork / 1e6;
      case 'TRIP.KMAEROWORK':
        return (vehicleData.sumAeroWork / 1e6 / vehicleData.sumDistance) * 100;
      case 'TRIP.STANDSTILLWORK':
        return vehicleData.sumStandStillTime / 1e6;
      case 'TRIP.ROLLWORK':
        return vehicleData.sumRollWork / 1e6;
      case 'TRIP.GRADEWORK':
        return vehicleData.sumGradeWork / 1e6;
      case 'TRIP.AVERAGESPEED':
        return vehicleData.tripResponse.calculation.averageSpeed * 3.6;
      case 'TRIP.FUELCONSUMPTION':
        return vehicleData.tripResponse.absoluteCalculation.fuelConsumption;
      case 'TRIP.G_CO2EMISSION':
        return (vehicleData.sumEmission / vehicleData.sumDistance) * 1000;
      case 'TRIP.PERCENTAGEWLTPLOW':
        return vehicleData.tripResponse.calculation.percentageWltpLow * 100;
      case 'TRIP.PERCENTAGEWLTPMEDIUM':
        return vehicleData.tripResponse.calculation.percentageWltpMedium * 100;
      case 'TRIP.PERCENTAGEWLTPHIGH':
        return vehicleData.tripResponse.calculation.percentageWltpHigh * 100;
      case 'TRIP.PERCENTAGEWLTPEXTRAHIGH':
        return (
          vehicleData.tripResponse.calculation.percentageWltpExtraHigh * 100
        );
      case 'TRIP.PERCENTAGEACCCYCLE':
        return vehicleData.tripResponse.calculation.percentageAccCycle * 100;
      default:
        return null;
    }
  }

  private formatValue(value: unknown, info: AttributeInfo): string {
    if (value === null || value === undefined) {
      return '';
    }

    switch (info.detailType) {
      case DetailType.number: {
        if (value === 0 || value) {
          const formattedValue = this.decimalPipe.transform(
            value as number,
            info.formatString,
            this.locale
          );

          return formattedValue || '';
        }
        return '';
      }

      case DetailType.boolean:
        return this.ts.instant(`TRIP.${value}`);

      case DetailType.date: {
        return (
          this.datePipe.transform(
            value as Date,
            info.formatString || 'dd.MM.yyyy, HH:mm',
            'UTC',
            this.locale
          ) || ''
        );
      }

      case DetailType.period: {
        if (!value) {
          return '';
        }
        return secondsToDuration2(value as number, this.locale);
      }

      case DetailType.string:
      default:
        return (value as { toString(): string })?.toString() || '';
    }
  }

  public getHeader(columnId: string): string {
    if (columnId === 'ROWLABEL') {
      return this.ts.instant('TRIP.VEHICLENAME');
    }

    let translationKey;
    let isAverage = false;

    switch (columnId) {
      case 'TRIP_COUNT':
        translationKey = 'TRIP.COUNT';
        break;
      case 'TRIP_DURATION':
        translationKey = 'TRIP.DURATION';
        break;
      case 'TRIP_DISTANCE':
        translationKey = 'TRIP.DISTANCE';
        break;
      case 'TRIP_CO2EMISSION':
        translationKey = 'TRIP.CO2EMISSION';
        break;
      case 'TRIP_ENERGYCONSUMPTION':
        translationKey = 'TRIP.ENERGYCONSUMPTION';
        break;
      case 'TRIP_ZEROFUELDISTANCE':
        translationKey = 'TRIP.ZEROFUELDISTANCE';
        break;
      case 'TRIP_STANDSTILLTIME':
        translationKey = 'TRIP.STANDSTILLTIME';
        break;
      case 'TRIP_ACCWORK':
        translationKey = 'TRIP.ACCWORK';
        break;
      case 'TRIP_AEROWORK':
        translationKey = 'TRIP.AEROWORK';
        break;
      case 'TRIP_KMAEROWORK':
        translationKey = 'TRIP.AEROWORK';
        isAverage = true;
        break;
      case 'TRIP_STANDSTILLWORK':
        translationKey = 'TRIP.STANDSTILLWORK';
        break;
      case 'TRIP_ROLLWORK':
        translationKey = 'TRIP.ROLLWORK';
        break;
      case 'TRIP_GRADEWORK':
        translationKey = 'TRIP.GRADEWORK';
        break;
      case 'TRIP_AVERAGESPEED':
        translationKey = 'TRIP.AVERAGESPEED';
        isAverage = true;
        break;
      case 'TRIP_FUELCONSUMPTION':
        translationKey = 'TRIP.FUELCONSUMPTION';
        isAverage = true;
        break;
      case 'TRIP_G_CO2EMISSION':
        translationKey = 'TRIP.CO2EMISSION';
        isAverage = true;
        break;
      case 'TRIP_PERCENTAGEWLTPLOW':
        translationKey = 'TRIP.PERCENTAGEWLTPLOW';
        isAverage = true;
        break;
      case 'TRIP_PERCENTAGEWLTPMEDIUM':
        translationKey = 'TRIP.PERCENTAGEWLTPMEDIUM';
        isAverage = true;
        break;
      case 'TRIP_PERCENTAGEWLTPHIGH':
        translationKey = 'TRIP.PERCENTAGEWLTPHIGH';
        isAverage = true;
        break;
      case 'TRIP_PERCENTAGEWLTPEXTRAHIGH':
        translationKey = 'TRIP.PERCENTAGEWLTPEXTRAHIGH';
        isAverage = true;
        break;
      case 'TRIP_PERCENTAGEACCCYCLE':
        translationKey = 'TRIP.PERCENTAGEACCCYCLE';
        isAverage = true;
        break;
      default:
        translationKey = '';
    }

    const attributeInfo: AttributeInfo[] = this.attributeInfos.filter(
      (attr) => attr.key === translationKey
    );

    let currency = '';
    if (
      attributeInfo &&
      attributeInfo[0] &&
      attributeInfo[0].currencies?.length
    ) {
      currency = ` (${attributeInfo[0].currencies[0]})`;
    }
    const baseText = this.ts.instant(translationKey) + currency;

    if (isAverage) {
      return `Ø ${baseText}`;
    }
    return `Σ ${baseText}`;
  }

  public isSortable(columnId: string): boolean {
    return columnId !== 'ROWLABEL';
  }

  public getColumnClass(index: number): string {
    return index % 2 === 0 ? 'even-column' : 'odd-column';
  }
}
